import React, { useCallback, useMemo, useState } from 'react';

import { Box } from '@tanium/react-box';
import { useLocale } from '@tanium/react-locale-context';
import { usePreviousValue } from '@tanium/react-use-previous-value';

import { useDataByXValue } from '../../hooks/useDataByXValue';
import { useDotLegendItems } from '../../hooks/useDotLegendItems';
import { useGeneratorOutput } from '../../hooks/useGeneratorOutput';
import { XAxisProps, XAxisTickProps, YAxisProps, YAxisTickProps } from '../../utils/ChartAxisProps';
import { ChartLegendProps, MultiPointValueSeriesProps } from '../../utils/ChartProps';
import { DataKeyType, DefaultDatum } from '../../utils/DataProps';
import { defaultDataKeyGetter, defaultLabelGetter, defaultValueGetter } from '../../utils/defaultGetters';
import { defaultXGetter, defaultYGetter } from '../../utils/defaultXYGetters';
import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { NumberCoercible } from '../../utils/NumberCoercible';
import { usePortal } from '../../utils/usePortal';
import { DefaultPointType } from '../../utils/XYDataProps';
import {
  activeConditionBadgeRenderer,
  ActiveConditionProps,
  ChartColorProps,
  ChartContainerProps,
  ContextLabelProps,
  contextLabelRenderer,
  StatusOrDataProps,
  withChartContainer,
  withColorScale,
} from '../ChartContainer';
import { Plot } from '../Plot';
import { getRangePoints } from '../Plot/Plot';
import { Svg } from '../Svg';
import { AnimationOverlay } from '../utils/AnimationOverlay';
import { Overlay } from '../utils/Overlay';
import { OverlayBottomAxisLine } from '../utils/OverlayBottomAxisLine';
import { OverlayVerticalAxisLine } from '../utils/OverlayVerticalAxisLine';
import { useCustomPathInterpolation } from './hooks/useCustomPathInterpolation';
import { useCustomPointTransition } from './hooks/useCustomPointTransition';
import { useInteractiveOverlay } from './hooks/useInteractiveOverlay';
import { useScales } from './hooks/useScales';
import { useShapeGenerator } from './hooks/useShapeGenerator';
import { useShapes } from './hooks/useShapes';
import { useTooltip } from './hooks/useTooltip';
import { Points } from './Points';
import { XYChartCommonProps, XYChartOwnProps } from './XYChartProps';

// TODO: Dynamically position chart components as part of https://jira.corp.tanium.com/browse/CHART-123
// for now we are removing the `margin` prop from chart components.
const MARGIN_INTERNAL = { bottom: 104, left: 72, right: 8, top: 16 } as const;

const DEFAULT_X_TICK_COUNT = 5;
const DEFAULT_Y_TICK_COUNT = 5;

const DEFAULT_LEGEND_HEIGHT_PX = 32;
const LEGEND_BOTTOM_PADDING_PX = 8;
const TOTAL_LEGEND_HEIGHT_PX = DEFAULT_LEGEND_HEIGHT_PX + LEGEND_BOTTOM_PADDING_PX;

/**
 * Additional props that are used by an XYChart that contains a plot.
 */
export interface XYChartWithPlotProps extends XAxisProps, XAxisTickProps, YAxisProps, YAxisTickProps {}

/**
 * Props that other components wrapping XYChart should pass through. Exposing this type separately
 * allows other components to avoid omitting props, which appears to improve type inference in
 * Storybook documentations.
 */
export type XYChartBaseProps<
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
> = XYChartCommonProps<TDataKeyType, TPointType, TDatum> &
  XYChartWithPlotProps &
  MultiPointValueSeriesProps<TDataKeyType, NumberCoercible, TPointType, TPointType[], TDatum>;

/**
 * All props accepted by the XYChart component.
 */
type XYChartInnerProps<
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
> = XYChartBaseProps<TDataKeyType, TPointType, TDatum> & XYChartOwnProps;

const XYChartInner = <
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
>({
  colorScale,
  data,
  dataKeyGetter = defaultDataKeyGetter,
  height,
  isAnimated = true,
  isInitialAnimationEnabled = true,
  labelGetter = defaultLabelGetter,
  onClick,
  onPointClick,
  segmentTransitionGenerator,
  shapeGeneratorFactory,
  shapeRenderer,
  showLegend = true,
  showPoints = true,
  showTooltipPercentage = true,
  showTooltipTime = true,
  showTooltipTotal = true,
  stacked = false,
  tooltipRenderer: tooltipRendererProp,
  tooltipTitle,
  valueGetter = defaultValueGetter,
  width,
  xAxisHeight: xAxisHeightProp,
  xDomainMax: xDomainMaxProp,
  xDomainMin: xDomainMinProp,
  xGetter = defaultXGetter,
  xTickFormatter: xTickFormatterProp,
  yAxisLabel,
  yDomainMax: yDomainMaxProp,
  yDomainMin: yDomainMinProp = 0,
  yGetter = defaultYGetter,
  yScaleType = 'linear',
  yTickFormatter,
}: XYChartInnerProps<TDataKeyType, TPointType, TDatum>): JSX.Element => {
  const prevData = usePreviousValue(data);
  // this should be checked every render
  const dataChanged = data !== prevData;

  const xAxisHeight = xAxisHeightProp ?? MARGIN_INTERNAL.bottom - TOTAL_LEGEND_HEIGHT_PX;

  const totalSpaceBelowPlot = xAxisHeight + (showLegend ? TOTAL_LEGEND_HEIGHT_PX : 0);

  const dataByXValue = useDataByXValue({ data, dataKeyGetter, valueGetter, xGetter, yGetter, sortByXValue: true });

  const { generatorOutput, filteredGeneratorOutput } = useGeneratorOutput({
    data,
    dataByXValue,
    dataKeyGetter,
    expand: false,
    reverseStack: true,
    stacked,
  });

  const { xScale, yScale, xDomainMax, xDomainMin, yDomainMin, yDomainMax } = useScales({
    dataByXValue,
    generatorOutput,
    height,
    margin: { ...MARGIN_INTERNAL, bottom: totalSpaceBelowPlot },
    width,
    xDomainMax: xDomainMaxProp,
    xDomainMin: xDomainMinProp,
    yDomainMax: yDomainMaxProp,
    yDomainMin: yDomainMinProp,
    yDomainRounding: true,
    yScaleType,
  });

  const { activePointInfo, guideline, interactiveOverlay } = useInteractiveOverlay({
    colorScale,
    data,
    filteredGeneratorOutput,
    labelGetter,
    onPointClick,
    valueGetter,
    xGetter,
    xScale,
    yGetter,
    yScale,
  });

  const { svgRef, tooltip } = useTooltip({
    activePointInfo,
    colorScale,
    labelGetter,
    showPercentage: showTooltipPercentage,
    showTime: showTooltipTime,
    showTotal: showTooltipTotal,
    sortData: !stacked,
    title: tooltipTitle,
    tooltipRenderer: tooltipRendererProp,
    xScale,
    yScale,
  });

  const { createPortal, portal } = usePortal();
  const { createPortal: createPointsAnimationPortal, portal: pointsAnimationPortal } = usePortal();
  const { createPortal: createShapesAnimationPortal, portal: shapesAnimationPortal } = usePortal();

  const [isInitialPointsAnimation, setIsInitialPointsAnimation] = useState(isAnimated);
  // Since shapes are always rendered, we intentionally disable the initial points animation once
  // the initial shapes animation has finished. This way, if `showPoints` is changed from false to
  // true after the initial render, the initial points animation won't be used without the
  // corresponding shapes animation.
  const handleOnShapesAnimationEnd = useCallback(() => setIsInitialPointsAnimation(false), []);

  // The legend should be created before shapes to ensure implicit color assignments are ordered as
  // expected. See CHART-237
  const { activeDataKey: legendActiveDataKey, legend } = useDotLegendItems({
    colorScale,
    data,
    dataKeyGetter,
    labelGetter,
    onClick,
    valueGetter,
  });

  const shapeGenerator = useShapeGenerator({
    xScale,
    yScale,
    xDomainMin,
    xDomainMax,
    yDomainMin,
    yDomainMax,
    shapeGeneratorFactory,
  });

  const pointTransitionsByKey = useCustomPointTransition<TDataKeyType, TPointType, TDatum>({
    dataChanged,
    generatorOutput,
    isAnimated,
    xScale,
    yScale,
  });

  const segmentedPathsByKey = useCustomPathInterpolation<TDataKeyType, TPointType, TDatum>({
    dataChanged,
    generatorOutput,
    isAnimated,
    pointTransitionsByKey,
    generateSegmentTransition: segmentTransitionGenerator,
  });

  const shapes = useShapes<TDataKeyType, TPointType, TDatum>({
    activeSeriesDataKey: activePointInfo?.seriesDataKey ?? legendActiveDataKey,
    colorScale,
    createPortal: createShapesAnimationPortal,
    dataChanged,
    generatorOutput,
    isAnimated,
    isInitialAnimationEnabled,
    onInitialAnimationEnd: handleOnShapesAnimationEnd,
    segmentedPathsByKey,
    shapeGenerator,
    shapeRenderer,
    yScale,
  });

  const points = showPoints && (
    <Points
      activeSeriesDataKey={legendActiveDataKey}
      activePointInfo={activePointInfo}
      colorScale={colorScale}
      createPortal={createPointsAnimationPortal}
      dataChanged={dataChanged}
      filteredGeneratorOutput={filteredGeneratorOutput}
      isAnimated={isAnimated}
      isInitialAnimationEnabled={isInitialAnimationEnabled && isInitialPointsAnimation}
      pointTransitionsByKey={pointTransitionsByKey}
      xScale={xScale}
      yScale={yScale}
    />
  );

  const xTickCount = xDomainMin === undefined && xDomainMax === undefined ? 0 : DEFAULT_X_TICK_COUNT;
  const yTickCount = yDomainMax === undefined ? 0 : DEFAULT_Y_TICK_COUNT;

  const locale = useLocale();
  const xTickFormatter = useMemo(() => {
    if (xTickFormatterProp) {
      return xTickFormatterProp;
    }

    const formatter = new Intl.DateTimeFormat(locale, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      timeZone: 'UTC',
    });

    return (d: NumberCoercible) => formatter.format(d.valueOf());
  }, [locale, xTickFormatterProp]);

  const { min: xMin, max: xMax } = useMemo(() => getRangePoints(xScale), [xScale]);
  const { min: yMin, max: yMax } = useMemo(() => getRangePoints(yScale), [yScale]);

  return (
    <>
      <Box height="100%" display="flex">
        <Svg width={width} height={height} ref={svgRef}>
          <Plot<NumberCoercible, number>
            isAnimated={isAnimated}
            createPortal={createPortal}
            xAxisHeight={xAxisHeight}
            xScale={xScale}
            yAxisWidth={MARGIN_INTERNAL.left}
            yScale={yScale}
            xTickCount={xTickCount}
            yTickCount={yTickCount}
            yAxisLabel={yAxisLabel}
            xTickFormatter={xTickFormatter}
            yTickFormatter={yTickFormatter}
          >
            {shapes}
          </Plot>
          {guideline}
          {/* Render points after Plot because we want points to appear above the axes */}
          {points}
          {interactiveOverlay}
        </Svg>
        {isAnimated && isInitialAnimationEnabled && (
          <AnimationOverlay width={width} height={height}>
            {shapesAnimationPortal}
            {isInitialPointsAnimation && (
              <>
                <OverlayBottomAxisLine xMin={xMin} xMax={xMax} width={width} plotBottom={yMax} />
                <OverlayVerticalAxisLine yMin={yMin} yMax={yMax} height={height} plotLeft={xMin} />
              </>
            )}
            {pointsAnimationPortal}
          </AnimationOverlay>
        )}
      </Box>
      {tooltip}
      <Overlay width={width} height={height}>
        {portal}
      </Overlay>
      {showLegend && (
        <Box position="absolute" style={{ top: height - TOTAL_LEGEND_HEIGHT_PX }} width="100%">
          {legend}
        </Box>
      )}
    </>
  );
};

/**
 * Props that may be passed to an XYChart.
 */
export type XYChartProps<
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
> = StatusOrDataProps<
  'data',
  ExtendAndOverride<
    ChartColorProps<TDataKeyType> &
      ChartContainerProps &
      ChartLegendProps &
      ContextLabelProps &
      ActiveConditionProps<TDatum>,
    XYChartInnerProps<TDataKeyType, TPointType, TDatum>
  >
>;

/**
 * An explicit type declaration for the XYChart component. This is mostly useful to preserve generic
 * type parameters when memoizing the underlying component.
 */
type XYChartComponentType = <
  TDataKeyType extends DataKeyType,
  TPointType = DefaultPointType,
  TDatum = DefaultDatum<TPointType[], TDataKeyType>,
>(
  props: XYChartProps<TDataKeyType, TPointType, TDatum>,
) => JSX.Element;

/**
 * A chart that renders arbitrary shapes and points for one or more series along linear x- and
 * y-axes.
 */
export const XYChart = React.memo(
  withColorScale(
    withChartContainer(XYChartInner, {
      footerRenderFns: [contextLabelRenderer, activeConditionBadgeRenderer],
    }),
  ),
) as unknown as XYChartComponentType;
